import { useCallback, useRef, useState, useEffect } from "react";

import type { GqlObj } from "../graphql/types";

export type Unreliable<T> = T extends object
  ?
      | {
          [P in keyof T]?: Unreliable<T[P]> | null;
        }
      | null
  : T | null;

export const useMountedState = <T>(initial: T): [T, (arg: T) => void] => {
  const [state, setState] = useState<T>(initial);
  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return [
    state,
    useCallback((newValue: T) => {
      if (mounted.current) setState(newValue);
    }, []),
  ];
};

export function hasOwnProperty<TObj extends object, TKey extends PropertyKey>(
  obj: unknown,
  prop: TKey,
): obj is TObj & Record<TKey, unknown> {
  return (
    typeof obj === "object" &&
    !!obj &&
    Object.prototype.hasOwnProperty.call(obj, prop)
  );
}

export const includeGroup = (
  mutationObj: GqlObj,
  queryObj: GqlObj,
): boolean => {
  if (!mutationObj.directives.groups && !queryObj.directives.groups)
    return true;
  return !!mutationObj.directives.groups?.some((group) =>
    queryObj.directives.groups?.includes(group),
  );
};
